import React__default, { useReducer, useRef, useMemo, useCallback, createContext, useContext } from 'react';
import { Key, EventListener } from '@shopify/polaris';
import { useOnValueChange } from '@shopify/react-hooks';
import { LiveRegion } from '@dnd-kit/accessibility';
import { getSelectedItemNode, getInteractiveElement, getAnnouncementForElement } from './utilities.mjs';
import { Action } from './types.mjs';
import { useUniqueId } from '../../utilities/unique-id/hooks.mjs';

const KeyboardNavigationContext = /*#__PURE__*/createContext(null);
function getInitialState() {
  return {
    selectedIndex: -1,
    options: new Map(),
    announcement: ''
  };
}

// Tracks items wrapped with KeyboardNavigationOption
// Updates `selected` item in response to increment/decrement keypress
function reducer(state, action) {
  switch (action.type) {
    case Action.RegisterKeyboardNavigationOption:
      {
        const newOptions = new Map(state.options.entries());
        newOptions.set(action.payload.id, action.payload);
        return {
          ...state,
          options: newOptions
        };
      }
    case Action.UnregisterKeyboardNavigationOption:
      {
        const newOptions = new Map(state.options.entries());
        newOptions.delete(action.payload.id);
        const itemCount = newOptions.size;
        return {
          ...state,
          selectedIndex: state.selectedIndex != null && state.selectedIndex >= itemCount ? itemCount - 1 : state.selectedIndex,
          options: newOptions
        };
      }
    case Action.IncrementSelectedIndex:
      {
        const itemCount = state.options.size;
        const incremented = state.selectedIndex + 1;
        return {
          ...state,
          selectedIndex: incremented >= itemCount ? 0 : incremented
        };
      }
    case Action.DecrementSelectedIndex:
      {
        const itemCount = state.options.size;
        const decremented = state.selectedIndex - 1;
        return {
          ...state,
          selectedIndex: decremented < 0 ? itemCount - 1 : decremented
        };
      }
    default:
      return {
        ...state
      };
  }
}
const KeyboardNavigation = /*#__PURE__*/React__default.memo(function KeyboardNavigation({
  axis = 'y',
  children,
  onEscape,
  disabled
}) {
  const [state, dispatch] = useReducer(reducer, undefined, getInitialState);
  const optionsRef = useRef([]);
  const selectedItem = getSelectedItemNode(state.options, state.selectedIndex);
  const liveRegionId = useUniqueId('KeyboardNavigation-LiveRegion');
  useOnValueChange(selectedItem, () => {
    const interactiveElement = getInteractiveElement(selectedItem);
    if (!interactiveElement) {
      return;
    }
    scrollIntoView(interactiveElement);
  });
  const announcement = useMemo(() => {
    if (!selectedItem) {
      return null;
    }
    const interactiveElement = getInteractiveElement(selectedItem);
    return getAnnouncementForElement(interactiveElement);
  }, [selectedItem]);
  const handleEnter = useCallback(evt => {
    if (!selectedItem) {
      return;
    }
    evt.preventDefault();
    const interactiveElement = getInteractiveElement(selectedItem);
    interactiveElement === null || interactiveElement === void 0 ? void 0 : interactiveElement.click();
  }, [selectedItem]);
  const handleEscape = useCallback(evt => {
    if (onEscape) {
      evt.preventDefault();
      onEscape();
    }
  }, [onEscape]);
  const handleKeyDown = useCallback(event => {
    const keys = axis === 'x' ? {
      increment: Key.RightArrow,
      decrement: Key.LeftArrow
    } : {
      increment: Key.DownArrow,
      decrement: Key.UpArrow
    };
    switch (event.keyCode) {
      case keys.increment:
        event.preventDefault();
        dispatch({
          type: Action.IncrementSelectedIndex
        });
        break;
      case keys.decrement:
        event.preventDefault();
        dispatch({
          type: Action.DecrementSelectedIndex
        });
        break;
      case Key.Enter:
        handleEnter(event);
        break;
      case Key.Escape:
        handleEscape(event);
        break;
    }
  }, [axis, handleEnter, handleEscape]);

  // Clear options on every render before collecting latest options & index positions in `useKeyboardNavigation`
  // Collecting the index positions of KeyboardNavigationOptions during the render phase
  // of KeyboardNavigation ensures we have the correct order.
  // We rely on the index positions being sequential to navigate up and down the list.
  // TODO: refactor to useMutableSource when available
  optionsRef.current = [];
  const context = useMemo(() => ({
    state,
    dispatch,
    options: optionsRef
  }), [state]);
  return /*#__PURE__*/React__default.createElement(KeyboardNavigationContext.Provider, {
    value: context
  }, disabled ? null : /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement(LiveRegion, {
    id: liveRegionId,
    announcement: announcement ? announcement : ''
  }), /*#__PURE__*/React__default.createElement(EventListener, {
    event: "keydown",
    handler: handleKeyDown
  })), children);
});
function useKeyboardNavigationContext() {
  return useContext(KeyboardNavigationContext);
}
function scrollIntoView(interactiveElement) {
  // TODO: change to scrollIntoViewIfNeeded when it becomes a standard feature
  // https://developer.mozilla.org/en-US/docs/Web/API/Element/scrollIntoViewIfNeeded
  interactiveElement.scrollIntoView({
    block: 'nearest',
    inline: 'nearest'
  });
}

export { KeyboardNavigation, KeyboardNavigationContext, useKeyboardNavigationContext };
